import { AgencyId, SuretyAccountId, UserId } from "@inrev/common";
import { useAdminBrokerOfRecordAccount } from "@portal/api/account/admin";
import { AgencySelectorInput, AgentSelectorInput } from "@portal/components/form";
import { DatePickerInput } from "@portal/components/form/inputs/DatePickerInput";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Button } from "@portal/components/primitives";
import { FC, useState } from "react";

export type BrokerOfRecordAccountModalProps = {
	accountId: SuretyAccountId;
	currentAgencyId: AgencyId;
	onClose: () => void;
};

export const BrokerOfRecordAccountModal: FC<BrokerOfRecordAccountModalProps> = ({
	accountId,
	currentAgencyId,
	onClose,
}) => {
	const { brokerOfRecordAccount, brokerOfRecordAccountError, brokerOfRecordAccountLoading } =
		useAdminBrokerOfRecordAccount(accountId);

	const [agencyId, setAgencyId] = useState<AgencyId | null>(null);
	const [followerUserId, setFollowerUserId] = useState<UserId | null>(null);
	const [effectiveDate, setEffectiveDate] = useState<string>("");

	const handleAgencyChange = (value: AgencyId | null) => {
		setAgencyId(value);
		setFollowerUserId(null);
	};

	const handleSubmit = () => {
		if (agencyId && followerUserId && effectiveDate) {
			brokerOfRecordAccount({ agencyId, followerUserId, effectiveDate, onSuccess: onClose });
		}
	};

	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader
				header="Change Broker of Record"
				onClose={onClose}
				className="h-full max-h-[70vh]"
			>
				<div className="w-full h-full text-sm">
					<div className="flex w-[420px] min-w-[420px] p-6">
						<div className="relative flex flex-1 flex-col space-y-5">
							{brokerOfRecordAccountError && (
								<div data-testid="broker-of-record-account-error" className="text-red-600">
									Failed to change broker of record. Please try again.
								</div>
							)}
							<div className="flex flex-col space-y-1.5">
								<div
									data-testid="broker-of-record-account-agency-label"
									className="font-medium text-secondary"
								>
									New broker of record agency
								</div>
								<div onWheel={(e) => e.stopPropagation()}>
									<AgencySelectorInput
										testId="broker-of-record-account-agency-dropdown"
										value={agencyId}
										excludeAgencyId={currentAgencyId}
										onChange={handleAgencyChange}
										optionsClassName="max-h-[240px] overscroll-contain"
									/>
								</div>
							</div>
							<div className="flex flex-col space-y-1.5">
								<div
									data-testid="broker-of-record-account-follower-label"
									className="font-medium text-secondary"
								>
									Follower agent
								</div>
								<div onWheel={(e) => e.stopPropagation()}>
									<AgentSelectorInput
										testId="broker-of-record-account-follower-dropdown"
										agencyId={agencyId}
										value={followerUserId}
										onChange={(value) => setFollowerUserId(value)}
										optionsClassName="max-h-[240px] overscroll-contain"
									/>
								</div>
							</div>
							<div className="flex flex-col space-y-1.5">
								<div
									data-testid="broker-of-record-account-effective-date-label"
									className="font-medium text-secondary"
								>
									Effective date
								</div>
								<DatePickerInput
									value={effectiveDate || undefined}
									placeholder="Select date"
									onChange={(value) => setEffectiveDate(value)}
								/>
							</div>
							<div className="flex flex-1 items-end justify-end gap-2 pt-2 mt-auto">
								<Button
									data-testid="broker-of-record-account-confirm-button"
									className="order-2"
									color="primary"
									variant="solid"
									disabled={
										!agencyId || !followerUserId || !effectiveDate || brokerOfRecordAccountLoading
									}
									loading={brokerOfRecordAccountLoading}
									onClick={handleSubmit}
								>
									Confirm
								</Button>
								<Button
									data-testid="broker-of-record-account-cancel-button"
									className="order-1"
									color="secondary"
									variant="solid"
									onClick={onClose}
								>
									Cancel
								</Button>
							</div>
						</div>
					</div>
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
